<script setup lang="ts">
import ProgressFrame from './layout/ProgressFrame.vue';
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import pWaitFor from 'p-wait-for';
import { comApi } from '@/http/api';
import commissioningApiClient from '@/http/commissioningApiClient';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import {
  SOFTWARE_UPDATE_INTERVAL_MS,
  SOFTWARE_UPDATE_TIMEOUT_MS,
} from '../utils/constants';
import { calculateSwUpdateProgress } from '../utils/timed-progress-calculator';
import {
  decideSoftwareUpdateAction,
  normalizeSoftwareState,
  type SoftwareOtaState,
} from '../utils/software-update';

const commissioningDeviceStore = useCommissioningDeviceStore();

const timedProgress = ref(0);
const isConcluded = ref(false);
const isSuccessful = ref(false);
const intendedVersion = ref('');
// Skip re-POST while Redis has not yet shown the hop; reset on poll so
// a later idle tick can POST desired again after a gate hop finishes.
const applyIssued = ref(false);

const router = useRouter();
const pathForwardSuccess = () => {
  router.replace({ name: 'SoftwareUpdateSucceeded' });
};
const pathForwardFailure = () => {
  router.replace({ name: 'SoftwareUpdateFailed' });
};

/**
 * Fake the progress by incrementing a fixed amount every few seconds
 */
const startTimedProgress = () => {
  const INTERVAL_5_S = 5000;

  const interval = setInterval(() => {
    if (isConcluded.value) {
      clearInterval(interval);
      return;
    }
    const next = calculateSwUpdateProgress(timedProgress.value, INTERVAL_5_S);
    timedProgress.value = next;
  }, INTERVAL_5_S);
  return interval;
};

const fetchSoftwareState = async (): Promise<SoftwareOtaState> => {
  const { data } = await commissioningApiClient.get(
    comApi.getSolshareSoftware(commissioningDeviceStore.serialNumber),
  );
  return normalizeSoftwareState(data);
};

const applySoftwareVersion = async (version: string): Promise<void> => {
  await commissioningApiClient.post(
    comApi.applySolshareSoftware(commissioningDeviceStore.serialNumber),
    { version },
    {
      validateStatus: (status) => status === 204 || (status >= 200 && status < 300),
    },
  );
};

const tickUpdate = async (): Promise<boolean> => {
  const state = await fetchSoftwareState();
  if (!intendedVersion.value) {
    intendedVersion.value = (state.solshareVersion.desired || '').trim();
  }

  const action = decideSoftwareUpdateAction(state, intendedVersion.value);

  if (action.type === 'done') {
    isConcluded.value = true;
    isSuccessful.value = true;
    timedProgress.value = 100;
    return true;
  }

  if (action.type === 'fail') {
    isConcluded.value = true;
    isSuccessful.value = false;
    return true;
  }

  if (action.type === 'poll') {
    applyIssued.value = false;
    return false;
  }

  if (applyIssued.value) {
    return false;
  }

  await applySoftwareVersion(action.version);
  applyIssued.value = true;
  return false;
};

const pollUpdateStatus = async () => {
  try {
    await pWaitFor(
      async () => {
        const done = await tickUpdate();
        return done ? pWaitFor.resolveWith(true) : false;
      },
      {
        interval: SOFTWARE_UPDATE_INTERVAL_MS,
        timeout: SOFTWARE_UPDATE_TIMEOUT_MS,
        // Mount already ran one tick; avoid an immediate second POST/GET.
        before: false,
      },
    );

    if (isConcluded.value && isSuccessful.value) {
      commissioningDeviceStore.resetSoftwareUpdateState();
      pathForwardSuccess();
    } else {
      pathForwardFailure();
    }
  } catch (e) {
    console.error('Error updating software', e);
    pathForwardFailure();
  }
};

onMounted(async () => {
  try {
    // Resolve intended version and apply one hop if idle (short POST).
    // If a hop is already in progress, tickUpdate only polls.
    await tickUpdate();
  } catch (e) {
    console.error('Error triggering software update', e);
    pathForwardFailure();
    return;
  }

  startTimedProgress();
  if (!isConcluded.value) {
    pollUpdateStatus();
  } else if (isSuccessful.value) {
    commissioningDeviceStore.resetSoftwareUpdateState();
    pathForwardSuccess();
  } else {
    pathForwardFailure();
  }
});
</script>

<template>
  <ProgressFrame pageClass="fullfocus-page">
    <div class="content">
      <a-progress
        type="circle"
        :percent="Math.round(timedProgress)"
        :stroke-color="{
          '0%': 'var(--color-mid-tone)',
          '100%': 'var(--color-primary)',
        }"
        :width="158"
        class="progress-circle"
      >
        <template #format="percent">
          <span class="progress-circle__text value">{{ percent }}</span>
          <span class="progress-circle__text percentage">%</span>
        </template>
      </a-progress>

      <h2>Update in progress</h2>

      <p>
        Please wait and ensure the unit stays powered whilst we complete the
        update.
      </p>
      <p>This may take up to 20 minutes.</p>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.content {
  text-align: center;
}

.progress-circle {
  margin-bottom: 40px;

  .progress-circle__text {
    color: @color-primary;
    font-family: Helvetica Light, sans-serif;

    &.value {
      font-size: 40px;
    }
    &.percentage {
      font-size: 12px;
    }
  }
}
</style>
